import { Command } from 'commander';
import fs from 'fs-extra';
import path from 'path';
import chalk from 'chalk';
import ora from 'ora';
import Table from 'cli-table3';
import inquirer from 'inquirer';
import { 
  EARSParser, 
  EARSGenerator, 
  EARSTemplateGenerator,
  EARSType,
  EARSRequirement 
} from '../core/ears';
import { EARSTaskConverter } from '../core/ears';
import { TaskStore } from '../core/task-store';
import { logger } from '../utils/logger';

export function createEarsCommand(): Command {
  const command = new Command('ears');
  
  command
    .description('管理 EARS (Easy Approach to Requirements Syntax) 需求')
    .addCommand(createParseCommand())
    .addCommand(createGenerateCommand())
    .addCommand(createConvertCommand())
    .addCommand(createValidateCommand())
    .addCommand(createExamplesCommand());
  
  return command;
}

function createParseCommand(): Command {
  const command = new Command('parse');
  
  command
    .description('解析 EARS 格式的需求文档')
    .argument('<file>', '需求文档路径')
    .option('-o, --output <file>', '输出路径')
    .option('-f, --format <format>', '输出格式 (json/markdown)', 'markdown')
    .action(async (file, options) => {
      const spinner = ora('解析 EARS 需求...').start();
      
      try {
        // 读取文件
        const content = await fs.readFile(file, 'utf-8');
        
        // 解析需求
        const parser = new EARSParser();
        const requirements = parser.parseRequirements(content);
        
        spinner.succeed(`解析完成，找到 ${requirements.length} 个需求`);
        
        // 显示结果
        const table = new Table({
          head: ['ID', '类型', '系统', '需求', '条件'],
          style: { head: ['cyan'] }
        });
        
        for (const req of requirements) {
          const conditions = [
            req.when && `WHEN ${req.when}`,
            req.where && `WHERE ${req.where}`,
            req.while && `WHILE ${req.while}`,
            req.if && `IF ${req.if}`
          ].filter(Boolean).join(' ');
          
          table.push([
            req.id,
            chalk.blue(req.type),
            req.system,
            req.shall.substring(0, 40) + (req.shall.length > 40 ? '...' : ''),
            conditions || '-'
          ]);
        }
        
        console.log(table.toString());
        
        // 保存结果
        if (options.output) {
          if (options.format === 'json') {
            await fs.writeJson(options.output, requirements, { spaces: 2 });
          } else {
            const generator = new EARSGenerator();
            const doc = generator.generateDocument(requirements);
            await fs.writeFile(options.output, doc);
          }
          console.log(chalk.green(`\n结果已保存到: ${options.output}`));
        }
        
      } catch (error: any) {
        spinner.fail('解析失败');
        logger.error(error.message);
      }
    });
  
  return command;
}

function createGenerateCommand(): Command {
  const command = new Command('generate');
  
  command
    .description('生成 EARS 需求文档')
    .option('-t, --type <type>', 'EARS 类型', 'all')
    .option('-s, --system <name>', '系统名称', 'system')
    .option('-o, --output <file>', '输出文件')
    .option('-i, --interactive', '交互式生成')
    .action(async (options) => {
      try {
        const requirements: EARSRequirement[] = [];
        
        if (options.interactive) {
          // 交互式创建需求
          let addMore = true;
          
          while (addMore) {
            const answers = await inquirer.prompt([
              {
                type: 'list',
                name: 'type',
                message: '选择需求类型:',
                choices: [
                  { name: '普遍性需求 (Ubiquitous)', value: EARSType.UBIQUITOUS },
                  { name: '事件驱动需求 (Event-Driven)', value: EARSType.EVENT_DRIVEN },
                  { name: '状态驱动需求 (State-Driven)', value: EARSType.STATE_DRIVEN },
                  { name: '可选功能需求 (Optional)', value: EARSType.OPTIONAL },
                  { name: '不想要的行为 (Unwanted)', value: EARSType.UNWANTED }
                ]
              },
              {
                type: 'input',
                name: 'system',
                message: '系统/组件名称:',
                default: options.system
              },
              {
                type: 'input',
                name: 'shall',
                message: '需求描述 (shall...):',
                validate: (input) => input.trim() !== ''
              }
            ]);
            
            const req: EARSRequirement = {
              id: `REQ-${Date.now()}`,
              type: answers.type,
              system: answers.system,
              shall: answers.shall
            };
            
            // 根据类型添加额外字段
            if (answers.type === EARSType.EVENT_DRIVEN) {
              const { trigger } = await inquirer.prompt({
                type: 'input',
                name: 'trigger',
                message: 'WHEN (触发事件):'
              });
              req.when = trigger;
            } else if (answers.type === EARSType.STATE_DRIVEN) {
              const { state } = await inquirer.prompt({
                type: 'input',
                name: 'state',
                message: 'WHILE (状态):'
              });
              req.while = state;
            } else if (answers.type === EARSType.OPTIONAL) {
              const { condition } = await inquirer.prompt({
                type: 'input',
                name: 'condition',
                message: 'IF (条件):'
              });
              req.if = condition;
            }
            
            // 优先级
            const { priority } = await inquirer.prompt({
              type: 'list',
              name: 'priority',
              message: '优先级:',
              choices: ['must', 'should', 'could']
            });
            req.priority = priority as 'must' | 'should' | 'could';
            
            requirements.push(req);
            
            const { more } = await inquirer.prompt({
              type: 'confirm',
              name: 'more',
              message: '继续添加需求?',
              default: true
            });
            addMore = more;
          }
        } else {
          // 生成示例需求
          requirements.push(...EARSTemplateGenerator.generateExamples());
        }
        
        // 生成文档
        const generator = new EARSGenerator();
        const document = generator.generateDocument(requirements, {
          title: `${options.system} 需求规格`,
          includeMetadata: true,
          groupByType: true
        });
        
        // 显示或保存
        if (options.output) {
          await fs.writeFile(options.output, document);
          console.log(chalk.green(`\n需求文档已生成: ${options.output}`));
        } else {
          console.log('\n' + document);
        }
        
      } catch (error: any) {
        logger.error('生成需求失败:', error.message);
      }
    });
  
  return command;
}

function createConvertCommand(): Command {
  const command = new Command('convert');
  
  command
    .description('将 EARS 需求转换为任务')
    .argument('<file>', 'EARS 需求文件')
    .option('--no-tests', '不创建测试任务')
    .option('--no-docs', '不创建文档任务')
    .option('-p, --priority <priority>', '默认优先级', 'medium')
    .option('--dry-run', '仅预览，不创建任务')
    .action(async (file, options) => {
      const spinner = ora('转换 EARS 需求...').start();
      
      try {
        // 读取并解析需求
        const content = await fs.readFile(file, 'utf-8');
        const parser = new EARSParser();
        const requirements = parser.parseRequirements(content);
        
        if (requirements.length === 0) {
          spinner.fail('未找到有效的 EARS 需求');
          return;
        }
        
        // 转换为任务
        const converter = new EARSTaskConverter();
        const tasks = converter.convertRequirements(requirements, {
          includeTests: options.tests,
          includeDocumentation: options.docs,
          defaultPriority: options.priority
        });
        
        spinner.succeed(`转换完成，生成 ${tasks.length} 个任务`);
        
        // 显示任务
        const table = new Table({
          head: ['类型', '内容', '优先级', '预估工时', '标签'],
          style: { head: ['cyan'] }
        });
        
        for (const task of tasks) {
          table.push([
            task.section || '任务',
            task.content.substring(0, 50) + (task.content.length > 50 ? '...' : ''),
            chalk[task.priority === 'high' ? 'red' : task.priority === 'medium' ? 'yellow' : 'green'](task.priority),
            task.estimatedHours ? `${task.estimatedHours}h` : '-',
            (task.tags || []).join(', ')
          ]);
        }
        
        console.log(table.toString());
        
        // 创建任务
        if (!options.dryRun) {
          const { confirm } = await inquirer.prompt({
            type: 'confirm',
            name: 'confirm',
            message: `创建 ${tasks.length} 个任务?`,
            default: true
          });
          
          if (confirm) {
            const taskStore = new TaskStore();
            let created = 0;
            
            for (const task of tasks) {
              await taskStore.addTask(task);
              created++;
            }
            
            console.log(chalk.green(`\n✅ 成功创建 ${created} 个任务`));
            console.log('\n下一步:');
            console.log('- 查看任务: ' + chalk.cyan('workflow task list'));
            console.log('- 开始工作: ' + chalk.cyan('workflow next'));
          }
        }
        
      } catch (error: any) {
        spinner.fail('转换失败');
        logger.error(error.message);
      }
    });
  
  return command;
}

function createValidateCommand(): Command {
  const command = new Command('validate');
  
  command
    .description('验证 EARS 需求的格式和完整性')
    .argument('<file>', '需求文件')
    .action(async (file) => {
      const spinner = ora('验证 EARS 需求...').start();
      
      try {
        const content = await fs.readFile(file, 'utf-8');
        const lines = content.split('\n').filter(line => line.trim());
        const parser = new EARSParser();
        
        const results: Array<{
          line: number;
          text: string;
          valid: boolean;
          error?: string;
          requirement?: EARSRequirement;
        }> = [];
        
        // 逐行验证
        lines.forEach((line, index) => {
          if (line.startsWith('#') || line.startsWith('//') || line.trim() === '') {
            return;
          }
          
          const req = parser.parseRequirement(line);
          if (req) {
            results.push({
              line: index + 1,
              text: line,
              valid: true,
              requirement: req
            });
          } else {
            results.push({
              line: index + 1,
              text: line,
              valid: false,
              error: '无法识别的 EARS 格式'
            });
          }
        });
        
        spinner.stop();
        
        // 显示结果
        const validCount = results.filter(r => r.valid).length;
        const invalidCount = results.filter(r => !r.valid).length;
        
        console.log('\n' + chalk.bold('验证结果:'));
        console.log(chalk.green(`✓ 有效需求: ${validCount}`));
        if (invalidCount > 0) {
          console.log(chalk.red(`✗ 无效需求: ${invalidCount}`));
        }
        
        // 显示错误
        if (invalidCount > 0) {
          console.log('\n' + chalk.bold('格式错误:'));
          results.filter(r => !r.valid).forEach(r => {
            console.log(chalk.red(`Line ${r.line}: ${r.error}`));
            console.log(chalk.gray(`  ${r.text}`));
          });
        }
        
        // 显示统计
        if (validCount > 0) {
          const typeStats = results
            .filter(r => r.valid && r.requirement)
            .reduce((acc, r) => {
              const type = r.requirement!.type;
              acc[type] = (acc[type] || 0) + 1;
              return acc;
            }, {} as Record<string, number>);
          
          console.log('\n' + chalk.bold('需求类型统计:'));
          Object.entries(typeStats).forEach(([type, count]) => {
            console.log(`  ${type}: ${count}`);
          });
        }
        
      } catch (error: any) {
        spinner.fail('验证失败');
        logger.error(error.message);
      }
    });
  
  return command;
}

function createExamplesCommand(): Command {
  const command = new Command('examples');
  
  command
    .description('显示 EARS 语法示例')
    .option('-t, --type <type>', '指定类型')
    .action((options) => {
      console.log(chalk.bold('\n📝 EARS 语法示例\n'));
      
      const examples = [
        {
          type: EARSType.UBIQUITOUS,
          title: '普遍性需求 (Ubiquitous)',
          template: 'The <system> shall <response>',
          example: 'The authentication system shall encrypt all passwords using bcrypt',
          chinese: '认证系统应使用 bcrypt 加密所有密码'
        },
        {
          type: EARSType.EVENT_DRIVEN,
          title: '事件驱动需求 (Event-Driven)',
          template: 'WHEN <trigger> the <system> shall <response>',
          example: 'WHEN a new user registers the system shall send a confirmation email',
          chinese: '当新用户注册时，系统应发送确认邮件'
        },
        {
          type: EARSType.STATE_DRIVEN,
          title: '状态驱动需求 (State-Driven)',
          template: 'WHILE <state> the <system> shall <response>',
          example: 'WHILE the user is logged in the system shall display personalized content',
          chinese: '当用户处于登录状态时，系统应显示个性化内容'
        },
        {
          type: EARSType.OPTIONAL,
          title: '可选功能需求 (Optional)',
          template: 'IF <condition> THEN the <system> shall <response>',
          example: 'IF dark mode is enabled THEN the system shall use dark theme colors',
          chinese: '如果启用了深色模式，那么系统应使用深色主题'
        },
        {
          type: EARSType.COMPLEX,
          title: '复杂需求 (Complex)',
          template: 'WHEN <trigger> WHERE <location> WHILE <state> IF <condition> the <system> shall <response>',
          example: 'WHEN payment is submitted WHERE region is EU WHILE GDPR mode is active the system shall request explicit consent',
          chinese: '当提交付款时，在欧盟地区，且 GDPR 模式激活的情况下，系统应请求明确同意'
        },
        {
          type: EARSType.UNWANTED,
          title: '不想要的行为 (Unwanted)',
          template: 'The <system> shall NOT <unwanted behavior>',
          example: 'The API shall NOT expose internal error details in responses',
          chinese: 'API 不应在响应中暴露内部错误详情'
        }
      ];
      
      const filtered = options.type 
        ? examples.filter(e => e.type === options.type)
        : examples;
      
      for (const example of filtered) {
        console.log(chalk.blue(`## ${example.title}`));
        console.log(chalk.gray(`模板: ${example.template}`));
        console.log(chalk.green(`示例: ${example.example}`));
        console.log(chalk.yellow(`中文: ${example.chinese}`));
        console.log();
      }
      
      console.log(chalk.bold('💡 使用提示:'));
      console.log('1. 每个需求应该是一个完整的句子');
      console.log('2. 使用 shall 表示强制要求');
      console.log('3. 系统名称要具体明确');
      console.log('4. 响应描述要可测试、可验证');
      console.log('5. 避免使用模糊的词汇（如"快速"、"友好"等）');
    });
  
  return command;
}